'use client';

import type { Key } from 'react';
import { isNil } from 'es-toolkit';

import { ActiveChecklistFilterDropdown } from './active-checklist-filter-dropdown';

type FilterOption<T extends Key> = {
  readonly value: T;
  readonly label: string;
  readonly color?: string;
};

type ActiveChecklistFilterProps<T extends Key> = {
  readonly allOptions: readonly FilterOption<T>[];
  readonly selectedValues: readonly T[];
  readonly onRemoveOption: (value: T) => void;
  readonly filterLabel: string;
  readonly filterContent: React.ReactNode;
};

export function ActiveChecklistFilter<T extends Key>({
  allOptions,
  selectedValues,
  onRemoveOption,
  filterLabel,
  filterContent,
}: ActiveChecklistFilterProps<T>) {
  const selectedOptions: FilterOption<T>[] = [];

  for (const value of selectedValues) {
    const option = allOptions.find((opt) => opt.value === value);
    if (!isNil(option)) {
      selectedOptions.push({
        value: option.value,
        label: option.label,
        ...(!isNil(option.color) ? { color: option.color } : {}),
      });
    }
  }

  if (selectedOptions.length === 0) return null;

  const MAX_VISIBLE_OPTIONS = 3;
  const visibleOptions = selectedOptions.slice(0, MAX_VISIBLE_OPTIONS);
  const remainingCount = selectedOptions.length - MAX_VISIBLE_OPTIONS;
  const hasMoreOptions = remainingCount > 0;

  return (
    <ActiveChecklistFilterDropdown
      visibleOptions={visibleOptions}
      remainingCount={remainingCount}
      hasMoreOptions={hasMoreOptions}
      onRemoveOption={onRemoveOption}
      filterLabel={filterLabel}
      filterContent={filterContent}
    />
  );
}
